import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import BottomNav from '../BottomNav';
import theme from '../theme';

const placeholderStyle = `
  .chat-search::placeholder { color: rgba(255,255,255,0.6); }
`;

function BadgeVerificato({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 4, flexShrink: 0 }}>
      <path d="M8 1L2 3.5V8c0 3.3 2.5 5.8 6 7 3.5-1.2 6-3.7 6-7V3.5L8 1z" fill={theme.colors.primary}/>
      <path d="M5.5 8l2 2 3-3" stroke={theme.colors.white} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function Chat({ session, profile }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchConversations(); }, []);

  const getMyJobIds = async () => {
    const name = profile?.full_name || session.user.email;
    const { data } = await supabase.from('jobs').select('id').eq('author', name);
    return (data || []).map(j => j.id);
  };

  const resolveProfileByEmail = async (email) => {
    const { data, error } = await supabase.rpc('get_profile_by_email', { user_email: email });
    if (!error && data && data.length > 0) {
      return { displayName: data[0].full_name, avatarUrl: data[0].avatar_url };
    }
    const part = email.split('@')[0];
    return { displayName: part.charAt(0).toUpperCase() + part.slice(1), avatarUrl: null };
  };

  const fetchConversations = async () => {
    setLoading(true);

    const { data: asApplicant } = await supabase
      .from('applications')
      .select('*, jobs(title, author)')
      .eq('applicant_email', session.user.email)
      .not('status', 'eq', 'rifiutata');

    const myJobIds = await getMyJobIds();
    let asOwner = [];
    if (myJobIds.length > 0) {
      const { data } = await supabase
        .from('applications')
        .select('*, jobs(title, author)')
        .in('job_id', myJobIds)
        .not('status', 'eq', 'rifiutata');
      asOwner = data || [];
    }

    const { data: allProfiles } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url, identity_status');

    const all = [];

    for (const app of (asApplicant || [])) {
      const authorName = app.jobs?.author;
      const profileMatch = (allProfiles || []).find(p => p.full_name === authorName);
      all.push({
        conversationId: `app_${app.id}`,
        jobTitle: app.jobs?.title,
        displayName: authorName || '?',
        avatarUrl: profileMatch?.avatar_url || null,
        isVerified: profileMatch?.identity_status === 'verificato',
        role: 'candidato',
        appId: app.id,
        status: app.status,
        completed: app.completed,
        created_at: app.created_at,
      });
    }

    for (const app of (asOwner || [])) {
      const resolved = await resolveProfileByEmail(app.applicant_email);
      const profileMatch = (allProfiles || []).find(p => p.full_name === resolved.displayName);
      all.push({
        conversationId: `app_${app.id}`,
        jobTitle: app.jobs?.title,
        displayName: resolved.displayName,
        avatarUrl: resolved.avatarUrl,
        isVerified: profileMatch?.identity_status === 'verificato',
        role: 'committente',
        appId: app.id,
        status: app.status,
        completed: app.completed,
        created_at: app.created_at,
      });
    }

    const withLastMessage = await Promise.all(all.map(async conv => {
      const { data: msgs } = await supabase
        .from('messages')
        .select('content, created_at, sender_id, read, type')
        .eq('conversation_id', conv.conversationId)
        .order('created_at', { ascending: false })
        .limit(1);

      const { count } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .eq('conversation_id', conv.conversationId)
        .eq('read', false)
        .neq('sender_id', session.user.id);

      return {
        ...conv,
        lastMessage: msgs?.[0] || null,
        unread: count || 0,
      };
    }));

    withLastMessage.sort((a, b) => {
      if (b.unread !== a.unread) return b.unread - a.unread;
      const dateA = a.lastMessage?.created_at || a.created_at;
      const dateB = b.lastMessage?.created_at || b.created_at;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return new Date(dateB) - new Date(dateA);
    });

    setConversations(withLastMessage);
    setLoading(false);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'ora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}min fa`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h fa`;
    if (diff < 86400000 * 7) return d.toLocaleDateString('it-IT', { weekday: 'short' });
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  };

  const getDateGroup = (dateStr) => {
    if (!dateStr) return 'Più vecchi';
    const d = new Date(dateStr);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    if (d.toDateString() === now.toDateString()) return 'Oggi';
    if (d.toDateString() === yesterday.toDateString()) return 'Ieri';
    if (d > weekAgo) return 'Questa settimana';
    return 'Più vecchi';
  };

  const statusBadge = (conv) => {
    if (conv.completed) return { label: '🏆 Completato', bg: theme.colors.primaryLight, color: theme.colors.primary };
    if (conv.status === 'in attesa') return { label: '⏳ In attesa', bg: theme.colors.warningBgSoft, color: theme.colors.warningText };
    if (conv.status === 'controproposta') return { label: '💬 In negoziazione', bg: theme.colors.infoBg, color: theme.colors.info };
    if (conv.status === 'accettata') return { label: '✅ Accettata', bg: theme.colors.primaryLight, color: theme.colors.primary };
    return null;
  };

  const previewText = (conv) => {
    if (!conv.lastMessage) {
      // Nessun messaggio ancora oltre alla candidatura iniziale: il testo dipende da chi guarda
      return conv.role === 'candidato' ? '📋 Candidatura inviata' : '📋 Candidatura ricevuta';
    }
    const type = conv.lastMessage.type;
    if (type === 'application') {
      return conv.role === 'candidato' ? '📋 Candidatura inviata' : '📋 Nuova candidatura ricevuta';
    }
    if (type === 'offer') return '💼 Offerta formale inviata';
    if (type === 'completion_request') return '🎉 Accordo raggiunto';
    return conv.lastMessage.content;
  };

  const filtered = search.trim().length === 0
    ? conversations
    : conversations.filter(conv => {
        const q = search.toLowerCase();
        return conv.displayName?.toLowerCase().includes(q) ||
               conv.jobTitle?.toLowerCase().includes(q);
      });

  const grouped = filtered.reduce((groups, conv) => {
    const group = search.trim()
      ? 'Risultati'
      : getDateGroup(conv.lastMessage?.created_at || conv.created_at);
    if (!groups[group]) groups[group] = [];
    groups[group].push(conv);
    return groups;
  }, {});

  const groupOrder = search.trim()
    ? ['Risultati']
    : ['Oggi', 'Ieri', 'Questa settimana', 'Più vecchi'];

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family }}>
      <style>{placeholderStyle}</style>

      {/* Header */}
      <div style={{ background: theme.colors.primary, padding: '20px 20px 14px', color: theme.colors.white }}>
        <h1 style={{ margin: '0 0 4px', fontSize: theme.font.size.xxl }}>💬 Messaggi</h1>
        <p style={{ margin: `0 0 ${theme.spacing.md}px`, opacity: 0.85, fontSize: theme.font.size.base }}>
          {conversations.length > 0
            ? `${conversations.length} conversazioni attive`
            : 'Chat e candidature ricevute'}
        </p>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: theme.font.size.lg, color: 'rgba(255,255,255,0.7)' }}>🔍</span>
          <input
            className="chat-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca per nome o annuncio..."
            style={{
              width: '100%', padding: '9px 36px 9px 34px',
              borderRadius: theme.radius.md, border: 'none', fontSize: theme.font.size.md,
              boxSizing: 'border-box', background: 'rgba(255,255,255,0.2)',
              color: theme.colors.white, outline: 'none',
            }}
          />
          {search && (
            <span
              onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: theme.colors.white, fontSize: theme.font.size.xl }}>
              ✕
            </span>
          )}
        </div>
      </div>

      <div style={{ padding: theme.spacing.lg, paddingBottom: 80 }}>
        {loading && <p style={{ textAlign: 'center', color: theme.colors.textMuted }}>Caricamento...</p>}

        {!loading && conversations.length === 0 && (
          <div style={{ textAlign: 'center', marginTop: 60 }}>
            <div style={{ fontSize: 48, marginBottom: theme.spacing.lg }}>💬</div>
            <p style={{ color: theme.colors.textMuted, fontSize: theme.font.size.lg }}>Nessuna conversazione ancora.</p>
            <p style={{ color: '#bbb', fontSize: theme.font.size.base }}>Candidati a un lavoro o pubblica un annuncio per iniziare.</p>
          </div>
        )}

        {!loading && conversations.length > 0 && filtered.length === 0 && (
          <p style={{ textAlign: 'center', color: theme.colors.textMuted, marginTop: 40 }}>
            Nessun risultato per "<strong>{search}</strong>"
          </p>
        )}

        {groupOrder.map(group => {
          const convs = grouped[group];
          if (!convs || convs.length === 0) return null;
          return (
            <div key={group}>
              {!search.trim() && (
                <div style={{
                  fontSize: theme.font.size.xs, fontWeight: 700, color: '#aaa',
                  textTransform: 'uppercase', letterSpacing: 0.8,
                  margin: '16px 0 8px', paddingLeft: 4
                }}>
                  {group}
                </div>
              )}

              {convs.map(conv => {
                const badge = statusBadge(conv);
                const isUnread = conv.unread > 0;
                const isAttesa = conv.status === 'in attesa' && !conv.completed;

                return (
                  <div
                    key={conv.conversationId}
                    onClick={() => navigate(`/chat/${conv.conversationId}`, {
                      state: {
                        otherPerson: conv.displayName,
                        jobTitle: conv.jobTitle,
                        avatarUrl: conv.avatarUrl,
                        isVerified: conv.isVerified,
                      }
                    })}
                    style={{
                      display: 'flex', alignItems: 'center', gap: theme.spacing.md,
                      padding: '12px 14px', borderRadius: theme.radius.lg, marginBottom: 8,
                      border: isAttesa ? `1px solid ${theme.colors.warningBorder}` : '1px solid #e8e8e8',
                      cursor: 'pointer',
                      background: isUnread ? '#f0faf0' : isAttesa ? theme.colors.warningBgSoft : theme.colors.white,
                    }}>

                    <div style={{
                      width: 46, height: 46, borderRadius: theme.radius.circle,
                      background: theme.colors.primaryLight, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontWeight: 700, color: theme.colors.primary, fontSize: theme.font.size.md,
                      flexShrink: 0, overflow: 'hidden',
                    }}>
                      {conv.avatarUrl
                        ? <img src={conv.avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : conv.displayName?.[0]?.toUpperCase() || '?'
                      }
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <span style={{
                          fontWeight: isUnread ? 700 : 600, fontSize: theme.font.size.lg,
                          display: 'flex', alignItems: 'center', gap: 4, color: '#222'
                        }}>
                          {conv.displayName}
                          {conv.isVerified && <BadgeVerificato size={13} />}
                        </span>
                        <span style={{ fontSize: theme.font.size.xs, color: '#bbb', flexShrink: 0, marginLeft: 8 }}>
                          {formatTime(conv.lastMessage?.created_at || conv.created_at)}
                        </span>
                      </div>

                      <div style={{
                        fontSize: theme.font.size.xs, color: theme.colors.primary, marginBottom: 3,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        fontWeight: 500
                      }}>
                        📋 {conv.jobTitle}
                      </div>

                      <div style={{
                        fontSize: theme.font.size.base, color: isUnread ? '#333' : theme.colors.textMuted,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        fontWeight: isUnread ? 600 : 400,
                        marginBottom: badge ? 4 : 0
                      }}>
                        {previewText(conv)}
                      </div>

                      {badge && (
                        <span style={{
                          display: 'inline-block',
                          fontSize: 10, background: badge.bg, color: badge.color,
                          padding: '2px 8px', borderRadius: theme.radius.sm, fontWeight: 600
                        }}>
                          {badge.label}
                        </span>
                      )}
                    </div>

                    {isUnread && (
                      <div style={{
                        background: theme.colors.error, color: theme.colors.white, borderRadius: theme.radius.circle,
                        width: 22, height: 22, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: theme.font.size.sm, fontWeight: 700, flexShrink: 0
                      }}>
                        {conv.unread > 9 ? '9+' : conv.unread}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <BottomNav />
    </div>
  );
}

export default Chat;